import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useSlackCRUDActions } from '@mm/state/actions/SlackCRUDActions';
import { selectSlackAccounts } from '@mm/state/selectors/slackSelectors';
import { landmineNames } from '@mm/constants/landmine-names';
import { SlackHeaderComponent } from './SlackHeaderComponent';
import { ReactComponent as SlackIcon } from 'assets/slack.svg';
import { ReactComponent as AlarmIcon } from '../../../icons/alarm-bell.svg';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { useConnectSlack } from './use-connect-slack';
import { IntegrationSectionProps } from '../../../IntegrationsPanel/IntegrationsPanel';
import { DeleteOutlined, SlackOutlined } from '@ant-design/icons';
import css from './SlackIntegrations.module.css';

export const SlackIntegrations = ({ section }: IntegrationSectionProps) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector(selectSlackAccounts);

  const { getAccounts, disconnectAccount } = useSlackCRUDActions();
  const { openGeneralConfirmModal } = useModalActions();
  const { connectSlack } = useConnectSlack();

  useEffect(() => {
    getAccounts(facebookPageId);
  }, []);

  const onDisconnect = (accountId: number) => {
    const teamName = accounts?.find(acc => acc.id === accountId)?.teamName;
    openGeneralConfirmModal({
      headerText: `Are you sure you want to disconnect ${teamName} account from Slack Notifications?`,
      messageText: '',
      onCloseHandler: ok => {
        if (ok) {
          disconnectAccount(accountId, facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      okButtonStyling: { red: true },
      cancelButtonText: 'Cancel',
      cancelButtonFirst: true,
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };

  const headerComponent = (
    <SlackHeaderComponent
      isAccountConnected={!!accounts.length}
      accountsCount={accounts.length}
    />
  );

  const sectionProps = {
    landmineName: landmineNames?.[section?.landmineName],
    featureFlag: section?.requiredFeature,
  };

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="Slack Notifications"
          subtitle="Track customers activity with Slack"
          icon={<SlackIcon />}
          headerRightComponent={headerComponent}
          css={{ icon: 'integrations__slack-header-icon' }}
          {...sectionProps}
        />
      }
      emptyStateConfig={{
        text: 'Never miss your leads with notifications on Slack',
        button: 'Add a Slack Account',
        onClick: connectSlack,
        icon: <AlarmIcon />,
        ...sectionProps,
      }}
      showEmptyState={!accounts.length}
    >
      {!!accounts.length && (
        <div className={css.list}>
          {accounts.map(item => (
            <div className={css.listItem} key={item.id}>
              <div className={css.listIcon}>
                <SlackOutlined />
              </div>

              <span className={css.listTitle}>{item.teamName} Workspace</span>
              <span className={css.listActions}>
                <div className={css.listAction}>
                  <div
                    className={css.listActionIcon}
                    onClick={() => onDisconnect(item.id)}
                  >
                    <DeleteOutlined />
                  </div>
                </div>
              </span>
            </div>
          ))}
        </div>
      )}
    </BaseIntegration>
  );
};
